import React, { useState } from 'react';
import { Smile, Frown, Clock, ChevronDown } from 'lucide-react';
import { SelectionCard, VipGuard, VipCopyBadge } from './SelectionCard';
import { kindLabel } from './selectionKind';

const num = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
// match_date may be ISO or DD/MM/YYYY
export const isoDate = (d) => (/^\d{2}\/\d{2}\/\d{4}$/.test(d || '') ? d.split('/').reverse().join('-') : d);
const firstKick = (picks = []) => picks.filter((p) => p.match_date && p.match_time).map((p) => `${isoDate(p.match_date)} ${p.match_time}`).sort()[0];
const kickLabel = (k) => (k ? `${k.slice(8, 10)}/${k.slice(5, 7)} ${k.slice(11, 16)}` : '');

export const selectionReturn = (s) => (s.status === 'archived' && s.result !== 'unverified' ? (['won', 'void'].includes(s.result) ? (s.won_amount || 0) : 0) - (s.stake || 0) : null);

// Collapsed row (amount + outcome left, title right); tap to expand the full selection
export const SelectionPreview = ({ sel, states, onShare, onDelete, onEdit }) => {
  const [open, setOpen] = useState(false);
  const ret = selectionReturn(sel);
  const pending = sel.status !== 'archived' || ['unverified', 'void'].includes(sel.result);
  const Icon = pending ? Clock : sel.result === 'won' ? Smile : Frown;
  const iconCls = pending ? 'text-gray-400' : sel.result === 'won' ? 'text-[#22C55E]' : 'text-red-400';
  const title = sel.description || (sel.source === 'link' ? `Di ${sel.owner_name}` : 'La mia selezione');
  const kick = firstKick(sel.picks);
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-[#F5A623]/30 bg-[#1A1A1A]" data-testid={`sel-preview-${sel.selection_id}`}>
      {sel.vip_copy && <VipGuard />}
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex text-left" data-testid={`sel-preview-toggle-${sel.selection_id}`}>
        <div className="w-24 shrink-0 flex flex-col items-center justify-center gap-0.5 py-3 border-r border-white/10 bg-[#161616]">
          <Icon className={`w-5 h-5 ${iconCls}`} />
          <p className="text-sm font-black text-white" data-testid={`sel-preview-amount-${sel.selection_id}`}>{num(sel.stake)}</p>
          {ret !== null && (
            <p className={`text-[11px] font-bold ${ret >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`} data-testid={`sel-preview-return-${sel.selection_id}`}>{ret > 0 ? '+' : ''}{num(ret)}</p>
          )}
        </div>
        <div className="flex-1 min-w-0 px-3 py-3">
          <p className="text-[11px] text-gray-500" data-testid={`sel-preview-kind-${sel.selection_id}`}>{kindLabel(sel)}{sel.booking_code ? ` · Codice ${sel.booking_code}` : ''}</p>
          {sel.vip_copy && <VipCopyBadge id={sel.selection_id} />}
          <p className="text-[15px] font-black text-white truncate">{title}</p>
          <p className="text-[11px] text-gray-500 truncate">{kick ? `Inizio: ${kickLabel(kick)}` : sel.created_at ? `Salvata: ${fmt(sel.created_at)}` : ''}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 self-center mr-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-white/10" data-testid={`sel-preview-body-${sel.selection_id}`}>
          <SelectionCard sel={sel} states={states} onShare={onShare} onDelete={onDelete} onEdit={onEdit} bare />
        </div>
      )}
    </div>
  );
};

export default SelectionPreview;
